import { Card, Typography } from 'antd';
import React, { useEffect, useState } from 'react';

import { TimeCounter } from '../../components/TimeCounter/TimeCounter';
import { firestore } from '../../firebase/firebase';
import { FirestoreCollection } from '../../firebase/firebase.models';
import {
  confirmMObywatelVerification,
  resetVerification,
  showUserModal,
  updateMObywatelCode,
} from '../MojaTablica/Account/Identity/ConfirmIdentity/mobywatelService';

import { AdminSafetyIndicator } from './AdminMObywatelVerification/AdminSafetyIndicator';
import { MObywatelRequestCard } from './AdminMObywatelVerification/MObywatelRequestCard';
import { RejectionModal } from './AdminMObywatelVerification/RejectionModal';
import { ResetConfirmationModal } from './AdminMObywatelVerification/ResetConfirmationModal';
import { MObywatelRequest, RejectionModalState } from './AdminMObywatelVerification/types';

const { Title, Text } = Typography;

export const AdminMObywatelVerification: React.FC = () => {
  const [requests, setRequests] = useState<MObywatelRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [codeInputs, setCodeInputs] = useState<{ [key: string]: string }>({});
  const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});
  const [nextCleanupTime, setNextCleanupTime] = useState<Date | null>(null);
  const [rejectionModal, setRejectionModal] = useState<RejectionModalState>({
    visible: false,
    requestId: '',
    reason: '',
    customMessage: '',
  });
  const [resetModal, setResetModal] = useState<{
    visible: boolean;
    requestId: string;
    userFullName: string;
  }>({
    visible: false,
    requestId: '',
    userFullName: '',
  });

  useEffect(() => {
    const unsubscribe = firestore
      .collection(FirestoreCollection.MOBYWATEL_REQUESTS)
      .orderBy('createdAt', 'desc')
      .onSnapshot((snapshot) => {
        const requestsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as MObywatelRequest[];
        setRequests(requestsData);
        setLoading(false);
      });

    // Calculate next cleanup time (every 5 minutes from now)
    const calculateNextCleanup = () => {
      const now = new Date();
      const nextCleanup = new Date(now.getTime() + 5 * 60 * 1000); // +5 minutes
      setNextCleanupTime(nextCleanup);
    };

    calculateNextCleanup();
    const cleanupInterval = setInterval(calculateNextCleanup, 5 * 60 * 1000); // Update every 5 minutes

    return () => {
      unsubscribe();
      clearInterval(cleanupInterval);
    };
  }, []);

  const handleCodeSubmit = async (requestId: string) => {
    const code = codeInputs[requestId];
    if (!code || code.length !== 6) {
      return;
    }

    setActionLoading({ ...actionLoading, [requestId]: true });
    try {
      await updateMObywatelCode(requestId, code);
      setCodeInputs({ ...codeInputs, [requestId]: '' });
    } catch (error) {
      console.error('Error updating code:', error);
    } finally {
      setActionLoading({ ...actionLoading, [requestId]: false });
    }
  };

  const handleConfirmVerification = async (requestId: string, accepted: boolean) => {
    if (!accepted) {
      // Show rejection modal instead of directly rejecting
      setRejectionModal({
        visible: true,
        requestId,
        reason: '',
        customMessage: '',
      });
      return;
    }

    setActionLoading({ ...actionLoading, [requestId]: true });
    try {
      await confirmMObywatelVerification(requestId, accepted);
    } catch (error) {
      console.error('Error confirming verification:', error);
    } finally {
      setActionLoading({ ...actionLoading, [requestId]: false });
    }
  };

  const handleRejectWithReason = async () => {
    const { requestId, reason, customMessage } = rejectionModal;

    if (!reason) {
      return; // Validation - must select a reason
    }

    if (reason === 'custom' && !customMessage.trim()) {
      return; // Validation - custom message required
    }

    setActionLoading({ ...actionLoading, [requestId]: true });
    try {
      await confirmMObywatelVerification(requestId, false, reason, customMessage);
      setRejectionModal({ visible: false, requestId: '', reason: '', customMessage: '' });
    } catch (error) {
      console.error('Error rejecting verification:', error);
    } finally {
      setActionLoading({ ...actionLoading, [requestId]: false });
    }
  };

  const handleShowUserModal = async (requestId: string) => {
    setActionLoading({ ...actionLoading, [requestId]: true });
    try {
      await showUserModal(requestId);
    } catch (error) {
      console.error('Error showing user modal:', error);
    } finally {
      setActionLoading({ ...actionLoading, [requestId]: false });
    }
  };

  const handleResetVerification = (requestId: string) => {
    const request = requests.find((r) => r.id === requestId);
    if (!request) return;

    const userFullName = `${request.userData.firstName} ${request.userData.lastName}`;
    setResetModal({
      visible: true,
      requestId,
      userFullName,
    });
  };

  const handleResetConfirm = async () => {
    const { requestId } = resetModal;
    setActionLoading({ ...actionLoading, [requestId]: true });
    try {
      await resetVerification(requestId);
      setResetModal({ visible: false, requestId: '', userFullName: '' });
    } catch (error) {
      console.error('Error resetting verification:', error);
    } finally {
      setActionLoading({ ...actionLoading, [requestId]: false });
    }
  };

  const handleResetCancel = () => {
    setResetModal({ visible: false, requestId: '', userFullName: '' });
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <Title level={2} className="!mb-2">
              Weryfikacja mObywatel
            </Title>
            <Text type="secondary">Zarządzaj weryfikacjami tożsamości użytkowników</Text>
          </div>
          {nextCleanupTime && (
            <div className="text-right">
              <Text type="secondary" className="text-xs block">
                Następny cleanup za:
              </Text>
              <TimeCounter
                startTime={new Date()}
                countdownDuration={Math.max(0, Math.floor((nextCleanupTime.getTime() - Date.now()) / 1000))}
                prefix="🔄"
                suffix=""
                className="text-sm font-mono"
                warningThresholds={{
                  yellow: 120,
                  red: 60,
                }}
              />
            </div>
          )}
        </div>
      </div>

      <AdminSafetyIndicator requests={requests} />

      <div className="space-y-4">
        {loading ? (
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} loading className="w-full" />
            ))}
          </div>
        ) : requests.length === 0 ? (
          <Card className="text-center py-8">
            <Text type="secondary">Brak aktywnych weryfikacji</Text>
          </Card>
        ) : (
          requests.map((request) => (
            <MObywatelRequestCard
              key={request.id}
              request={request}
              codeInputs={codeInputs}
              setCodeInputs={setCodeInputs}
              actionLoading={actionLoading}
              onCodeSubmit={handleCodeSubmit}
              onConfirmVerification={handleConfirmVerification}
              onShowUserModal={handleShowUserModal}
              onResetVerification={handleResetVerification}
            />
          ))
        )}
      </div>

      <RejectionModal
        rejectionModal={rejectionModal}
        setRejectionModal={setRejectionModal}
        onReject={handleRejectWithReason}
        loading={actionLoading[rejectionModal.requestId]}
      />

      <ResetConfirmationModal
        visible={resetModal.visible}
        onConfirm={handleResetConfirm}
        onCancel={handleResetCancel}
        loading={actionLoading[resetModal.requestId]}
        userFullName={resetModal.userFullName}
      />
    </div>
  );
};
